// useKeycloakAdapter.js
import { useRef, useState, useCallback } from "react";
import Keycloak from "keycloak-js";
import { toast } from "react-toastify";

export const useKeycloakAdapter = () => {
  const keycloakRef = useRef(null);

  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const initializeKeycloak = useCallback(
    async (realm, clientId, options = {}) => {
      try {
        setIsLoading(true);

        const serverUrl = process.env.REACT_APP_FOR_REALM_LOCAL;
        if (!serverUrl) throw new Error("Missing Keycloak server URL");

        // Create a Keycloak JS instance
        const keycloak = new Keycloak({
          url: serverUrl,
          realm,
          clientId,
        });

        keycloakRef.current = keycloak;

        // Default init options - don't trigger redirects for setup flow
        const initOptions = {
          onLoad: options.onLoad || "check-sso",
          silentCheckSsoFallback: options.silentCheckSsoFallback !== false,
          checkLoginIframe: false,
          flow: "standard",
          pkceMethod: 'S256',
          enableLogging: true,
          ...options
        };

        const authenticated = await keycloak.init(initOptions);

        setIsInitialized(true);
        setIsLoading(false);

        return { authenticated, keycloak };
      } catch (err) {
        const msg = `Keycloak init failed: ${err.message}`;
        console.error(msg);
        setIsLoading(false);
        setError(msg);
        throw err;
      }
    },
    []
  );

  /** -------------------------------
   * Helper: Open popup window for keycloak setup
   * --------------------------------
   */
  const openPopup = (url, name = "keycloak-popup") => {
    const width = 800;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      url,
      name,
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );

    if (!popup) {
      toast.error("Popup blocked. Enable popups.");
      return null;
    }

    return popup;
  };

  /** -------------------------------
   * LOGIN (full redirect or popup)
   * --------------------------------
   */
  const login = useCallback(
    (redirectUri = window.location.href) => {
      if (!keycloakRef.current) return toast.error("Keycloak not ready");

      const loginUrl = keycloakRef.current.createLoginUrl({
        redirectUri,
      });

      return openPopup(loginUrl, "keycloak-login");
    },
    []
  );

  /** ----------------------------------------------
   * RUN REQUIRED ACTION (UPDATE_PROFILE, TOTP, etc.)
   * This is the correct way to trigger account setup.
   * -----------------------------------------------
   */
  const runRequiredAction = useCallback(
    (action, redirectUri = window.location.href) => {
      if (!keycloakRef.current) {
        toast.error("Keycloak not ready");
        return;
      }

      const actionUrl = keycloakRef.current.createLoginUrl({
        kc_action: action, // UPDATE_PROFILE, CONFIGURE_TOTP, UPDATE_PASSWORD
        redirectUri,
      });

      return openPopup(actionUrl, "keycloak-required-action");
    },
    []
  );

  /** -------------------------------
   * TOKEN GETTERS
   * --------------------------------
   */
  const getToken = () => keycloakRef.current?.token || null;
  const getTokenParsed = () => keycloakRef.current?.tokenParsed || null;

  /** -------------------------------
   * CHECK TOKEN EXPIRATION
   * --------------------------------
   */
  const isTokenExpired = (minValidity = 10) =>
    keycloakRef.current?.isTokenExpired(minValidity) || false;

  /** -------------------------------
   * LOGOUT
   * --------------------------------
   */
  const logout = useCallback(
    (redirectUri = window.location.href) => {
      if (!keycloakRef.current) return;
      keycloakRef.current.logout({ redirectUri });
    },
    []
  );

  /** -------------------------------
   * OPEN ACCOUNT MANAGEMENT - TOTP SETUP
   * Open Keycloak TOTP configuration directly in popup
   * Window auto-closes after successful setup
   * --------------------------------
   */
  const openAccountManagement = useCallback((realm, clientId) => {
    try {
      const serverUrl = process.env.REACT_APP_FOR_REALM_LOCAL;
      if (!serverUrl) {
        toast.error('Keycloak server URL not configured');
        return null;
      }

      if (!realm) {
        toast.error('Realm not provided');
        return null;
      }

      // Use the static HTML page from public folder as redirect URI
      const currentOrigin = window.location.origin;
      const closePageUrl = `${currentOrigin}/setup-complete.html`;

      // Build the authorization URL that will redirect to TOTP setup after login
      // Using OAuth2 authorization code flow with PKCE
      const authParams = new URLSearchParams({
        client_id: clientId,
        redirect_uri: closePageUrl,
        response_type: 'code',
        scope: 'openid profile email',
        // Add kc_action to trigger TOTP setup
        kc_action: 'CONFIGURE_TOTP'
      });

      const authUrl = `${serverUrl}/realms/${realm}/protocol/openid-connect/auth?${authParams.toString()}`;
      
      console.log('Opening Keycloak auth URL:', authUrl);
      
      const popup = openPopup(authUrl, 'keycloak-totp-setup');
      
      return popup;
    } catch (err) {
      const errorMsg = `Failed to open account management: ${err.message}`;
      console.error(errorMsg, err);
      toast.error(errorMsg);
      return null;
    }
  }, []);

  return {
    keycloak: keycloakRef.current,
    isInitialized,
    isLoading,
    error,

    initializeKeycloak,

    // Main auth actions
    login,
    logout,
    runRequiredAction,
    openAccountManagement,

    // Token utilities
    getToken,
    getTokenParsed,
    isTokenExpired,
  };
};

export default useKeycloakAdapter;
